import { useState, useCallback, useEffect } from 'react';
import { loadRestaurants, createRestaurant, updateRestaurant, deleteRestaurant } from '../services/restaurantService';

/**
 * Custom hook for restaurant management
 * - CRUD operations
 * - Activity logging (automatic)
 * - File uploads support
 */
export function useRestaurants(currentUser, showToast) {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load restaurants when user changes
  useEffect(() => {
    if (currentUser) {
      loadRestaurantsData();
    }
  }, [currentUser]);

  /**
   * Load restaurants from database
   */
  const loadRestaurantsData = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const data = await loadRestaurants(currentUser);
      setRestaurants(data);
    } catch (err) {
      console.error('❌ Error loading restaurants:', err);
      if (showToast) {
        showToast('❌ فشل في تحميل المطاعم', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  /**
   * Create new restaurant
   */
  const handleCreate = useCallback(async (restaurantData) => {
    if (!currentUser) return null;
    
    try {
      const newRestaurant = await createRestaurant(restaurantData, currentUser);
      
      if (showToast) {
        showToast('✅ تم إضافة المطعم بنجاح');
      }
      
      // Reload restaurants
      await loadRestaurantsData();
      
      return newRestaurant;
    } catch (err) {
      console.error('❌ Error creating restaurant:', err);
      if (showToast) {
        showToast('❌ فشل في إضافة المطعم', 'error');
      }
      return null;
    }
  }, [currentUser, showToast, loadRestaurantsData]);

  /**
   * Update restaurant
   */
  const handleUpdate = useCallback(async (id, changes) => {
    if (!currentUser) return false;
    
    try {
      const success = await updateRestaurant(id, changes, currentUser);
      
      if (success) {
        if (showToast) {
          showToast('✅ تم التحديث بنجاح');
        }
        
        // Reload restaurants
        await loadRestaurantsData();
      }
      
      return success;
    } catch (err) {
      console.error('❌ Error updating restaurant:', err);
      if (showToast) {
        showToast('❌ فشل في التحديث', 'error');
      }
      return false;
    }
  }, [currentUser, showToast, loadRestaurantsData]);

  /**
   * Delete restaurant
   */
  const handleDelete = useCallback(async (id) => {
    try {
      const success = await deleteRestaurant(id);
      
      if (success) {
        if (showToast) {
          showToast('✅ تم حذف المطعم');
        }
        
        // Remove from local state immediately
        setRestaurants(prev => prev.filter(r => r.id !== id));
      }
      
      return success;
    } catch (err) {
      console.error('❌ Error deleting restaurant:', err);
      if (showToast) {
        showToast('❌ فشل في الحذف', 'error');
      }
      return false;
    }
  }, [showToast]);

  return {
    restaurants,
    loading,
    loadRestaurants: loadRestaurantsData,
    createRestaurant: handleCreate,
    updateRestaurant: handleUpdate,
    deleteRestaurant: handleDelete,
  };
}
